import colorsPalettesRaw from 'constants/colorsPalettes';
import type { ColorsPalette } from 'models/colorsPalette';

import { applyNcaambPaletteFallback } from './colorsPalettes';

export interface ColorSchema {
    primaryRgba: string;
    secondaryRgba: string;
    primaryButtonColor: string;
    secondaryButtonColor: string;
    textColor: string;
}

export const colorsPalettes: Record<string, ColorsPalette> = applyNcaambPaletteFallback(colorsPalettesRaw.colors || {});

export const convertHexWithAlphaToHex = (hexWithAlpha: string, isDarkMode?: boolean): string => {
    if (!hexWithAlpha || hexWithAlpha.length < 9 || hexWithAlpha[0] !== '#') {
        if (hexWithAlpha === 'inverse') {
            return isDarkMode ? 'white' : 'black';
        }
        if (hexWithAlpha === 'inverse_inverse') {
            return isDarkMode ? 'black' : 'white';
        }
        return hexWithAlpha;
    }

    const hex = hexWithAlpha.slice(3);
    const alpha = parseInt(hexWithAlpha.slice(1, 3), 16) / 255;

    return `rgba(${parseInt(hex.slice(0, 2), 16)}, ${parseInt(hex.slice(2, 4), 16)}, ${parseInt(
        hex.slice(4, 6),
        16,
    )}, ${alpha})`;
};

export const getColorSchemaFromPalette = (
    colorsPalette: ColorsPalette | Partial<ColorsPalette>,
    isDarkMode?: boolean,
): ColorSchema => {
    // Early return if colorsPalette is undefined
    if (!colorsPalette) {
        return {
            primaryRgba: '',
            secondaryRgba: '',
            primaryButtonColor: '',
            secondaryButtonColor: '',
            textColor: '',
        };
    }

    return {
        primaryRgba: convertHexWithAlphaToHex(
            isDarkMode ? colorsPalette.primaryDarkColor || '' : colorsPalette.primaryColor || '',
            isDarkMode,
        ),
        secondaryRgba: convertHexWithAlphaToHex(
            isDarkMode ? colorsPalette.secondaryDarkColor || '' : colorsPalette.secondaryColor || '',
            isDarkMode,
        ),
        primaryButtonColor: convertHexWithAlphaToHex(colorsPalette.primaryButtonColor || '', isDarkMode),
        secondaryButtonColor: convertHexWithAlphaToHex(colorsPalette.secondaryButtonColor || '', isDarkMode),
        textColor: convertHexWithAlphaToHex(colorsPalette.textColor || '', isDarkMode),
    };
};

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
    // Remove the # if present
    const cleanHex = hex.replace('#', '');

    // Validate hex string length
    if (cleanHex.length !== 6) {
        console.warn(`Invalid hex color: ${hex}`);
        return { r: 0, g: 0, b: 0 };
    }

    // Parse the hex values
    const r = parseInt(cleanHex.substring(0, 2), 16);
    const g = parseInt(cleanHex.substring(2, 4), 16);
    const b = parseInt(cleanHex.substring(4, 6), 16);

    // Check for NaN values
    if (isNaN(r) || isNaN(g) || isNaN(b)) {
        console.warn(`Invalid hex color: ${hex}`);
        return { r: 0, g: 0, b: 0 };
    }

    return { r, g, b };
};

const cssVarCache = new Map<string, { r: number; g: number; b: number }>();

if (typeof document !== 'undefined') {
    new MutationObserver(() => {
        cssVarCache.clear();
    }).observe(document.documentElement, {
        attributes: true,
        attributeFilter: ['class', 'data-theme', 'data-color-scheme'],
    });
}

const parseColor = (color: string): { r: number; g: number; b: number } => {
    if (color.startsWith('var(')) {
        if (typeof document !== 'undefined') {
            const cached = cssVarCache.get(color);
            if (cached) return cached;
            const varName = color.match(/var\((--[^)]+)\)/)?.[1];
            if (varName) {
                const resolved = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
                if (resolved) {
                    const result = parseColor(resolved);
                    cssVarCache.set(color, result);
                    return result;
                }
            }
        }
        // Server-side or unresolvable: return neutral fallback without warning.
        return { r: 0, g: 0, b: 0 };
    }
    if (color.startsWith('rgba(') || color.startsWith('rgb(')) {
        const match = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/);
        if (match) {
            return {
                r: parseInt(match[1]),
                g: parseInt(match[2]),
                b: parseInt(match[3]),
            };
        }
    }
    return hexToRgb(color);
};

const calculateColorDistance = (color1: string, color2: string): number => {
    const rgb1 = parseColor(color1);
    const rgb2 = parseColor(color2);

    // Calculate euclidean distance in RGB space
    const rDiff = rgb1.r - rgb2.r;
    const gDiff = rgb1.g - rgb2.g;
    const bDiff = rgb1.b - rgb2.b;
    return Math.sqrt(rDiff * rDiff + gDiff * gDiff + bDiff * bDiff);
};

// Picks black or white — whichever is more legible on top of an arbitrary fill
// (hex, rgb/rgba, or CSS var). Uses the WCAG relative-luminance formula and the
// standard 0.179 crossover threshold (the luminance at which black and white text
// have equal contrast). Guarantees a readable label on any fill, including light
// kits (yellow/white team colors) where a hardcoded white would disappear.
export const getReadableTextColor = (background: string): '#000000' | '#FFFFFF' => {
    const { r, g, b } = parseColor(background);
    const toLinear = (channel: number): number => {
        const s = channel / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    const luminance = 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b);
    return luminance > 0.179 ? '#000000' : '#FFFFFF';
};

export const areColorsTooSimilar = (color1: string, color2: string, threshold: number = 70): boolean => {
    // Early exit if colors are identical
    if (!color1 || !color2 || color1 === color2) {
        return true;
    }

    // Early exit if colors are not valid (rgb, rgba, or hex)
    if (!/^rgba?\(/.test(color1) && !/^#/.test(color1) && !/^rgba?\(/.test(color2) && !/^#/.test(color2)) {
        return false;
    }

    const distance = calculateColorDistance(color1, color2);
    return distance < threshold;
};
